const LOG_LEVEL_ORDER = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
};

const activeLogLevel =
    LOG_LEVEL_ORDER[(process.env.REACT_APP_LOG_LEVEL || 'info').toLowerCase()] || LOG_LEVEL_ORDER.info;

const REDACTED_KEYS = new Set([
    'authorization',
    'password',
    'current_password',
    'new_password',
    'token',
    'access_token',
    'refresh_token',
]);

function sanitize(value, depth = 0) {
    if (value == null) {
        return value;
    }
    if (depth > 4) {
        return '[MAX_DEPTH_REACHED]';
    }
    if (Array.isArray(value)) {
        return value.map((item) => sanitize(item, depth + 1));
    }
    if (typeof value === 'object') {
        const output = {};
        for (const [key, nested] of Object.entries(value)) {
            output[key] = REDACTED_KEYS.has(key.toLowerCase()) ? '[REDACTED]' : sanitize(nested, depth + 1);
        }
        return output;
    }
    return value;
}

function stringify(value) {
    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }
    if (typeof value === 'string') {
        return value.includes(' ') ? JSON.stringify(value) : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (value instanceof Error) {
        return `${value.name}:${value.message}`;
    }
    try {
        return JSON.stringify(value);
    } catch {
        return '[UNSERIALIZABLE]';
    }
}

function formatContext(context) {
    if (!context || typeof context !== 'object') {
        return '';
    }

    const entries = Object.entries(context);
    if (entries.length === 0) {
        return '';
    }

    return ` | ${entries.map(([key, value]) => `${key}=${stringify(value)}`).join(' ')}`;
}

function formatLine({ level, module, event, context }) {
    const timestamp = new Date().toISOString();
    const normalizedLevel = String(level || 'info').toUpperCase();
    return `[${timestamp}] ${normalizedLevel} (${module}) ${event}${formatContext(context)}`;
}

function emit(level, payload) {
    if (LOG_LEVEL_ORDER[level] < activeLogLevel) {
        return;
    }

    const line = formatLine({ level, ...payload });

    if (level === 'error') {
        console.error(line);
        return;
    }
    if (level === 'warn') {
        console.warn(line);
        return;
    }
    console.log(line);
}

export function CreateLogger(moduleName) {
    return {
        debug(event, context = {}) {
            emit('debug', { module: moduleName, event, context: sanitize(context) });
        },
        info(event, context = {}) {
            emit('info', { module: moduleName, event, context: sanitize(context) });
        },
        warn(event, context = {}) {
            emit('warn', { module: moduleName, event, context: sanitize(context) });
        },
        error(event, context = {}) {
            emit('error', { module: moduleName, event, context: sanitize(context) });
        },
    };
}
